import { useState, useEffect, useRef } from 'react';
import {
  ChevronDown,
  ChevronUp,
  Headphones,
  Mail,
  MessageCircle,
  BookOpen,
  Users,
  MapPin,
  Route,
  Bell,
  Navigation,
  CheckCircle,
  AlertCircle,
  Info,
  Search,
  X,
  Bus,
  UserCog,
  Star,
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Helmet } from 'react-helmet-async';

// ─── Search index ─────────────────────────────────────────────────────────────

const ALL_ITEMS = [
  {
    id: 'create-student',
    section: 'students',
    question: 'Comment créer un élève et son parent en même temps ?',
    keywords: 'formulaire ajouter nouveau créer',
  },
  {
    id: 'student-fields',
    section: 'students',
    question: 'Quels sont les champs obligatoires pour un élève ?',
    keywords: 'champs prénom nom date naissance latitude longitude gps coordonnées',
  },
  {
    id: 'parent-fields',
    section: 'students',
    question: 'Quels sont les champs obligatoires pour un parent ?',
    keywords: 'email téléphone prénom nom compte connexion login',
  },
  {
    id: 'gps-address',
    section: 'students',
    question: "Comment obtenir les coordonnées GPS d'une adresse ?",
    keywords: 'google maps latitude longitude coordonnées copier',
  },
  {
    id: 'existing-parent',
    section: 'students',
    question: 'Comment associer un élève à un parent existant ?',
    keywords: 'associer fratrie parent compte existant rechercher',
  },
  {
    id: 'csv-import',
    section: 'students',
    question: 'Comment importer plusieurs élèves en masse (CSV) ?',
    keywords: 'import csv masse fichier modèle template bulk importation',
  },
  {
    id: 'create-stop',
    section: 'stops',
    question: 'Comment créer un arrêt de bus avec géolocalisation ?',
    keywords: 'créer ajouter arrêt géolocalisation carte mapbox coordonnées',
  },
  {
    id: 'stop-fields',
    section: 'stops',
    question: 'Quels sont les champs obligatoires pour un arrêt ?',
    keywords: 'champs nom code adresse latitude longitude heure ramassage dépose',
  },
  {
    id: 'stop-gps',
    section: 'stops',
    question: "Comment obtenir les coordonnées GPS précises d'un arrêt ?",
    keywords: 'google maps coordonnées précision décimales',
  },
  {
    id: 'stop-types',
    section: 'stops',
    question: "Quels types d'arrêts existe-t-il ?",
    keywords: 'principal secondaire temporaire type',
  },
  {
    id: 'stop-edit',
    section: 'stops',
    question: 'Comment modifier ou supprimer un arrêt ?',
    keywords: 'modifier supprimer éditer crayon corbeille',
  },
  {
    id: 'stop-pending-approve',
    section: 'stops',
    question: 'Comment valider ou rejeter un arrêt proposé par un chauffeur ?',
    keywords: 'chauffeur proposer arrêt en attente valider rejeter pending',
  },
  {
    id: 'create-circuit',
    section: 'circuits',
    question: 'Comment créer un circuit de bus ?',
    keywords: 'créer circuit route onglets chauffeur assignation',
  },
  {
    id: 'circuit-fields',
    section: 'circuits',
    question: 'Quels sont les champs obligatoires pour un circuit ?',
    keywords: 'champs code nom arrêts bus chauffeur assigné',
  },
  {
    id: 'circuit-types',
    section: 'circuits',
    question: 'Quels types de circuits existe-t-il ?',
    keywords: 'régulier express spécial type',
  },
  {
    id: 'circuit-stops',
    section: 'circuits',
    question: "Comment ajouter ou réordonner les arrêts d'un circuit ?",
    keywords: 'ajouter réordonner glisser déposer ordre distance durée',
  },
  {
    id: 'assign-student',
    section: 'circuits',
    question: 'Comment assigner un élève à un circuit et un arrêt ?',
    keywords: 'affecter assigner élève circuit arrêt montée descente',
  },
  {
    id: 'create-driver',
    section: 'drivers',
    question: 'Comment créer un compte chauffeur ?',
    keywords: 'créer ajouter chauffeur compte',
  },
  {
    id: 'driver-fields',
    section: 'drivers',
    question: 'Quels sont les champs obligatoires pour un chauffeur ?',
    keywords: 'champs prénom nom email téléphone statut',
  },
  {
    id: 'assign-driver',
    section: 'drivers',
    question: 'Comment assigner un chauffeur à un circuit ?',
    keywords: 'assigner affecter circuit chauffeur',
  },
  {
    id: 'driver-app',
    section: 'drivers',
    question: 'Le chauffeur a-t-il accès à une application mobile ?',
    keywords: 'app application mobile chauffeur premium pointage absences',
    premium: true,
  },
  {
    id: 'create-bus',
    section: 'buses',
    question: 'Comment ajouter un bus à la flotte ?',
    keywords: 'créer ajouter bus flotte immatriculation',
  },
  {
    id: 'bus-fields',
    section: 'buses',
    question: 'Quels sont les champs obligatoires pour un bus ?',
    keywords: 'immatriculation modèle capacité places statut',
  },
  {
    id: 'bus-gps',
    section: 'buses',
    question: 'Comment lier le traceur GPS à un bus ?',
    keywords: 'traceur gps imei id boîtier lier configurer',
  },
  {
    id: 'bus-offline',
    section: 'buses',
    question: 'Pourquoi un bus apparaît-il hors ligne sur la carte ?',
    keywords: 'hors ligne offline signal perdu 15 minutes fraîcheur',
  },
  {
    id: 'how-gps',
    section: 'tracking',
    question: 'Comment fonctionne le suivi GPS des bus ?',
    keywords: 'gps suivi temps réel position carte iot traccar webhook',
  },
  {
    id: 'bus-not-on-map',
    section: 'tracking',
    question: "Que faire si un bus n'apparaît pas sur la carte ?",
    keywords: 'carte absent disparu bus manquant actif circuit',
  },
  {
    id: 'trip-history',
    section: 'tracking',
    question: "Comment consulter l'historique des trajets ?",
    keywords: 'historique trajets filtre date bus circuit premium replay',
    premium: true,
  },
  {
    id: 'parent-confirmation-check',
    section: 'tracking',
    question: "Que signifie le petit check vert à côté d'un élève dans le Suivi GPS / l'Historique ?",
    keywords: 'check vert confirmation parent élève à bord récupéré tap application',
  },
  {
    id: 'manual-notif',
    section: 'notifications',
    question: 'Comment envoyer une annonce aux parents ou aux chauffeurs ?',
    keywords: 'notification manuelle annonce envoyer message titre contenu destinataires chauffeur personnalisé',
    premium: true,
  },
  {
    id: 'auto-notifs',
    section: 'notifications',
    question: 'Quelles notifications sont envoyées automatiquement ?',
    keywords: 'automatique départ arrêt approche montée descente basic premium',
  },
  {
    id: 'notif-tiers',
    section: 'notifications',
    question: 'Quelle est la différence de notifications entre Basic et Premium ?',
    keywords: 'basic premium différence géofencing 150m confirmation chauffeur',
  },
  {
    id: 'holidays-notifs',
    section: 'notifications',
    question: 'Pourquoi les parents ne reçoivent-ils pas de notifications un certain jour ?',
    keywords: 'notification absente bloquée vacances jours fériés paramètres',
  },
  {
    id: 'live-eta',
    section: 'notifications',
    question: "Qu'est-ce que le suivi du bus en direct sur l'écran verrouillé du parent ?",
    keywords: 'écran verrouillé suivi direct live activity dynamic island android 16 iphone temps réel',
  },
  {
    id: 'admin-realtime-notif',
    section: 'notifications',
    question: 'Comment recevoir des notifications instantanées sur le dashboard admin ?',
    keywords: 'admin temps réel badge toast web push navigateur incident retard demande arrêt proposé',
    premium: true,
  },
  {
    id: 'holidays-setup',
    section: 'general',
    question: 'Comment bloquer les trajets et notifications pendant les vacances scolaires ?',
    keywords: 'vacances scolaires jours fériés configurer paramètres bloquer',
  },
  {
    id: 'dashboard',
    section: 'premium',
    question: 'Comment utiliser le tableau de bord analytique ?',
    keywords: 'dashboard analytique kpi statistiques graphiques taux ponctualité',
    premium: true,
  },
  {
    id: 'reports',
    section: 'premium',
    question: 'Comment accéder aux rapports de flotte ?',
    keywords: 'rapports statistiques flotte export données',
    premium: true,
  },
  {
    id: 'requests',
    section: 'premium',
    question: 'Comment gérer les demandes des parents ?',
    keywords: 'demandes parents absence changement arrêt valider refuser',
    premium: true,
  },
  {
    id: 'tier-comparison',
    section: 'general',
    question: 'Quelle est la différence entre les offres Basic et Premium ?',
    keywords: 'offre basic premium comparaison fonctionnalité tableau',
  },
  {
    id: 'tier-limits',
    section: 'general',
    question: 'Quelles sont les limites par offre ?',
    keywords: 'limite maximum élèves chauffeurs basic premium 100 300 quota',
  },
  {
    id: 'reset-password',
    section: 'general',
    question: 'Comment réinitialiser mon mot de passe ?',
    keywords: 'mot de passe oublié réinitialiser email lien 24h',
  },
  {
    id: 'multi-device',
    section: 'general',
    question: 'Comment accéder au dashboard depuis un autre appareil ?',
    keywords: 'navigateur mobile tablette appareil connexion chrome safari',
  },
];

const SECTIONS = [
  {
    id: 'students',
    label: 'Élèves & Parents',
    icon: Users,
    iconColor: 'text-indigo-600',
    bgColor: 'bg-indigo-100',
  },
  {
    id: 'stops',
    label: 'Arrêts de bus',
    icon: MapPin,
    iconColor: 'text-green-600',
    bgColor: 'bg-green-100',
  },
  {
    id: 'circuits',
    label: 'Circuits',
    icon: Route,
    iconColor: 'text-orange-600',
    bgColor: 'bg-orange-100',
  },
  {
    id: 'drivers',
    label: 'Chauffeurs',
    icon: UserCog,
    iconColor: 'text-sky-600',
    bgColor: 'bg-sky-100',
  },
  {
    id: 'buses',
    label: 'Bus & Flotte',
    icon: Bus,
    iconColor: 'text-rose-600',
    bgColor: 'bg-rose-100',
  },
  {
    id: 'tracking',
    label: 'Suivi GPS',
    icon: Navigation,
    iconColor: 'text-teal-600',
    bgColor: 'bg-teal-100',
  },
  {
    id: 'notifications',
    label: 'Notifications',
    icon: Bell,
    iconColor: 'text-purple-600',
    bgColor: 'bg-purple-100',
  },
  {
    id: 'premium',
    label: 'Fonctionnalités Premium',
    icon: Star,
    iconColor: 'text-amber-600',
    bgColor: 'bg-amber-100',
  },
  {
    id: 'general',
    label: 'Questions générales',
    icon: BookOpen,
    iconColor: 'text-gray-600',
    bgColor: 'bg-gray-100',
  },
];

// ─── Utility components ───────────────────────────────────────────────────────

const Step = ({ number, title, children }) => (
  <div className="flex gap-4">
    <div className="flex-shrink-0 w-7 h-7 rounded-full bg-blue-600 text-white text-xs font-bold flex items-center justify-center mt-0.5">
      {number}
    </div>
    <div>
      <p className="text-sm font-semibold text-gray-900 mb-1">{title}</p>
      {children && <div className="text-sm text-gray-600 leading-relaxed">{children}</div>}
    </div>
  </div>
);

const Field = ({ name, required, description }) => (
  <div className="flex items-start gap-2 py-1.5 border-b border-gray-100 last:border-0">
    <span className="text-sm font-medium text-gray-800 min-w-[180px]">
      {name}
      {required && <span className="text-red-500 ml-1">*</span>}
    </span>
    <span className="text-sm text-gray-500">{description}</span>
  </div>
);

const Tip = ({ children }) => (
  <div className="flex gap-2 p-3 bg-blue-50 border border-blue-100 rounded-lg mt-3">
    <Info className="w-4 h-4 text-blue-500 flex-shrink-0 mt-0.5" />
    <p className="text-sm text-blue-700">{children}</p>
  </div>
);

const Warning = ({ children }) => (
  <div className="flex gap-2 p-3 bg-amber-50 border border-amber-100 rounded-lg mt-3">
    <AlertCircle className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" />
    <p className="text-sm text-amber-700">{children}</p>
  </div>
);

const PremiumBadge = () => (
  <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700 text-xs font-semibold flex-shrink-0 ml-2">
    <Star className="w-2.5 h-2.5" />
    Premium
  </span>
);

// ─── Accordion (controlled) ───────────────────────────────────────────────────

const AccordionItem = ({ id, question, premium, open, onToggle, children }) => (
  <div
    id={id}
    className={`border rounded-lg overflow-hidden scroll-mt-4 ${premium ? 'border-amber-200 border-l-[3px] border-l-amber-300' : 'border-gray-200'}`}
  >
    <button
      onClick={onToggle}
      className="w-full flex items-center justify-between px-5 py-4 text-left bg-white hover:bg-gray-50 transition-colors"
    >
      <span className="flex items-center flex-1 min-w-0">
        <span className="text-sm font-medium text-gray-900">{question}</span>
        {premium && <PremiumBadge />}
      </span>
      {open ? (
        <ChevronUp className="w-4 h-4 text-gray-400 flex-shrink-0 ml-4" />
      ) : (
        <ChevronDown className="w-4 h-4 text-gray-400 flex-shrink-0 ml-4" />
      )}
    </button>
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: 'auto', opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          transition={{ duration: 0.2 }}
        >
          <div className="px-5 py-4 bg-gray-50 border-t border-gray-200">{children}</div>
        </motion.div>
      )}
    </AnimatePresence>
  </div>
);

// ─── Content map ──────────────────────────────────────────────────────────────

const CONTENT = {
  'create-student': (
    <div className="space-y-4">
      <p className="text-sm text-gray-600">
        Le formulaire M'EyeBus permet de créer un élève et son parent en une seule opération :
      </p>
      <div className="space-y-3">
        <Step number="1" title="Accéder à la section Élèves">
          Dans le menu latéral, cliquez sur <strong>Élèves</strong>, puis sur le bouton{' '}
          <strong>"Ajouter élève + parent"</strong> en haut à droite.
        </Step>
        <Step number="2" title="Remplir les informations de l'élève">
          Complétez les champs obligatoires (marqués d'un <span className="text-red-500">*</span>) dans l'onglet{' '}
          <strong>Élève</strong>.
        </Step>
        <Step number="3" title="Renseigner l'adresse de prise en charge">
          Dans la section <strong>Adresse</strong>, entrez l'adresse complète et les coordonnées GPS
          (latitude/longitude) du domicile.
        </Step>
        <Step number="4" title="Associer ou créer le parent">
          Choisissez <strong>"Nouveau parent"</strong> ou <strong>"Parent existant"</strong> et remplissez les
          informations.
        </Step>
        <Step number="5" title="Valider">
          Cliquez sur <strong>"Enregistrer"</strong>. L'élève et le parent sont créés simultanément.
        </Step>
      </div>
    </div>
  ),

  'student-fields': (
    <div className="space-y-2">
      <Field name="Prénom" required description="Prénom de l'élève" />
      <Field name="Nom" required description="Nom de famille de l'élève" />
      <Field name="Date de naissance" required description="Format JJ/MM/AAAA" />
      <Field name="Adresse de prise en charge" required description="Adresse complète du domicile" />
      <Field name="Latitude" required description="Coordonnée GPS — ex: 33.9716" />
      <Field name="Longitude" required description="Coordonnée GPS — ex: -6.8498" />
      <Field name="Genre" description="Masculin / Féminin" />
      <Field name="Classe" description="Ex: CE2 A" />
      <Field name="Statut" description="Actif / Inactif" />
      <Tip>Les coordonnées GPS sont obligatoires pour l'assignation à un arrêt et les alertes de proximité.</Tip>
    </div>
  ),

  'parent-fields': (
    <div className="space-y-2">
      <Field name="Prénom" required description="Prénom du parent" />
      <Field name="Nom" required description="Nom de famille du parent" />
      <Field name="Email" required description="Adresse email — ex: parent@email.com" />
      <Field name="Téléphone principal" required description="Format international — ex: +212 6 12 34 56 78" />
      <Field name="Contact d'urgence" description="Nom de la personne à contacter en cas d'urgence" />
      <Field name="Téléphone d'urgence" description="Numéro du contact d'urgence" />
      <Tip>
        L'email du parent est son identifiant de connexion à l'application mobile. Assurez-vous qu'il est valide et
        unique.
      </Tip>
    </div>
  ),

  'gps-address': (
    <div className="space-y-3">
      <p className="text-sm text-gray-600">Plusieurs méthodes pour obtenir la latitude et la longitude :</p>
      <div className="space-y-3">
        <Step number="1" title="Via Google Maps (recommandé)">
          Ouvrez <strong>maps.google.com</strong>, recherchez l'adresse, faites un <strong>clic droit</strong> sur le
          point, puis cliquez sur les coordonnées affichées pour les copier.
        </Step>
        <Step number="2" title="Via l'URL Google Maps">
          Les coordonnées apparaissent dans l'URL sous la forme{' '}
          <code className="bg-gray-100 px-1 rounded text-xs">@33.9716,-6.8498</code>.
        </Step>
        <Step number="3" title="Sur mobile">
          Appuyez longuement sur un point de la carte. Les coordonnées s'affichent en bas de l'écran.
        </Step>
      </div>
      <Warning>
        La latitude est toujours le premier chiffre (ex: 33.97). Pour le Maroc, la longitude est négative (ex: -6.84).
      </Warning>
    </div>
  ),

  'existing-parent': (
    <div className="space-y-3">
      <p className="text-sm text-gray-600">Si le parent a déjà un compte dans le système :</p>
      <div className="space-y-3">
        <Step number="1" title="Ouvrir le formulaire d'ajout d'élève" />
        <Step number="2" title="Sélectionner 'Parent existant'">
          Un champ de recherche apparaît. Tapez le nom ou l'email du parent pour le retrouver.
        </Step>
        <Step number="3" title="Sélectionner le parent dans la liste déroulante" />
        <Step number="4" title="Enregistrer" />
      </div>
      <Tip>
        Un parent peut être associé à plusieurs élèves (fratrie). Chaque élève a son propre profil mais partage le même
        compte parent.
      </Tip>
    </div>
  ),

  'csv-import': (
    <div className="space-y-3">
      <p className="text-sm text-gray-600">M'EyeBus supporte l'import en masse via fichier CSV :</p>
      <div className="space-y-3">
        <Step number="1" title="Télécharger le modèle CSV">
          Dans la section Élèves, cliquez sur <strong>"Importer"</strong> puis <strong>"Télécharger le modèle"</strong>.
        </Step>
        <Step number="2" title="Remplir le fichier">
          Complétez chaque ligne avec les informations d'un élève. Respectez les formats de colonnes.
        </Step>
        <Step number="3" title="Importer le fichier">
          Glissez-déposez le fichier ou cliquez pour le sélectionner. Le système valide les données avant l'import.
        </Step>
        <Step number="4" title="Corriger les erreurs si nécessaire">
          Les erreurs sont listées ligne par ligne. Corrigez le fichier et relancez l'import.
        </Step>
      </div>
      <Warning>
        Les coordonnées GPS sont obligatoires dans le CSV. Un import sans latitude/longitude sera rejeté.
      </Warning>
    </div>
  ),

  'create-stop': (
    <div className="space-y-4">
      <p className="text-sm text-gray-600">
        La création d'un arrêt se fait sur une page unique : carte interactive à gauche, formulaire à droite.
      </p>
      <div className="space-y-3">
        <Step number="1" title="Accéder à la section Arrêts">
          Dans le menu latéral, cliquez sur <strong>Arrêts</strong>, puis sur <strong>"Ajouter un arrêt"</strong>.
        </Step>
        <Step number="2" title="Placer le repère sur la carte">
          Cliquez directement sur la carte pour poser le repère, ou utilisez le bouton{' '}
          <strong>"Utiliser ma position"</strong>. Vous pouvez aussi saisir latitude et longitude manuellement puis
          cliquer sur <strong>"Centrer sur ces coordonnées"</strong>.
        </Step>
        <Step number="3" title="Remplir les informations">
          Dans le panneau à droite, renseignez le <strong>nom</strong>, le <strong>code</strong> et l'
          <strong>adresse complète</strong> (champs obligatoires). Les champs ville, code postal, quartier et horaires
          sont optionnels.
        </Step>
        <Step number="4" title="Assigner des élèves (optionnel)">
          Cliquez sur <strong>"Assigner des élèves"</strong> pour sélectionner les élèves qui montent à cet arrêt.
          Cliquez <strong>"Confirmer la sélection"</strong> pour valider, ou <strong>"Annuler"</strong> pour ignorer les
          changements.
        </Step>
        <Step number="5" title="Créer l'arrêt">
          Cliquez sur <strong>"Créer l'arrêt"</strong>, vérifiez le récapitulatif, puis confirmez.
        </Step>
      </div>
    </div>
  ),

  'stop-fields': (
    <div className="space-y-2">
      <Field name="Nom de l'arrêt" required description="Ex: Arrêt École Centrale" />
      <Field name="Code" required description="Identifiant court, max 20 caractères. Ex: ARR001" />
      <Field name="Adresse complète" required description="Ex: 123 Avenue Mohammed V" />
      <Field
        name="Coordonnées GPS"
        required
        description="Placées via la carte ou saisies manuellement (latitude entre -90 et 90, longitude entre -180 et 180)"
      />
      <Field name="Code postal" description="Optionnel" />
      <Field name="Ville" description="Optionnel" />
      <Field name="Quartier / district" description="Optionnel" />
      <Field name="Horaire ramassage (matin)" description="Optionnel — format HH:MM" />
      <Field name="Horaire dépose (soir)" description="Optionnel — format HH:MM" />
    </div>
  ),

  'stop-gps': (
    <div className="space-y-3">
      <p className="text-sm text-gray-600">Plusieurs méthodes pour placer un arrêt avec précision :</p>
      <div className="space-y-3">
        <Step number="1" title="Clic direct sur la carte (recommandé)">
          Depuis la page de création d'arrêt, cliquez directement sur l'emplacement voulu sur la carte Mapbox. Le repère
          se place instantanément.
        </Step>
        <Step number="2" title="Géolocalisation automatique">
          Cliquez sur <strong>"Utiliser ma position"</strong> pour placer le repère à votre position GPS actuelle. Utile
          si vous êtes sur le terrain.
        </Step>
        <Step number="3" title="Google Maps — clic droit">
          Naviguez jusqu'à l'emplacement, faites un <strong>clic droit</strong> sur le point exact, copiez les
          coordonnées affichées, puis collez-les dans les champs Latitude / Longitude de la page.
        </Step>
        <Step number="4" title="Sur le terrain avec un smartphone">
          Appuyez longuement sur l'emplacement exact dans Google Maps. Les coordonnées s'affichent en bas de l'écran.
        </Step>
      </div>
      <Tip>Privilégiez 6 décimales (ex: 33.971623, -6.849812) pour un positionnement précis.</Tip>
      <Warning>Pour le Maroc, la latitude est positive (30 à 36) et la longitude est négative (-1 à -13).</Warning>
    </div>
  ),

  'stop-types': (
    <div className="space-y-2">
      <Field name="Principal" description="Arrêt avec forte affluence, point de regroupement principal" />
      <Field name="Secondaire" description="Arrêt avec affluence modérée" />
      <Field name="Temporaire" description="Arrêt saisonnier ou ponctuel" />
      <Tip>
        Le type est informatif et n'affecte pas le fonctionnement du circuit. Il aide à organiser les arrêts sur la
        carte.
      </Tip>
    </div>
  ),

  'stop-edit': (
    <div className="space-y-3">
      <p className="text-sm text-gray-600">
        <strong>Modifier :</strong> Dans la liste des arrêts, ouvrez le menu <strong>⋯</strong> sur la ligne concernée,
        puis cliquez sur <strong>"Modifier"</strong>. La page s'ouvre avec toutes les informations pré-remplies.
      </p>
      <p className="text-sm text-gray-600">
        <strong>Supprimer :</strong> Dans le même menu <strong>⋯</strong>, cliquez sur <strong>"Supprimer"</strong>. Une
        confirmation est demandée avec la liste des dépendances.
      </p>
      <Warning>
        La localisation GPS ne peut pas être modifiée si l'arrêt est assigné à un circuit. Retirez-le d'abord de tous
        les circuits pour modifier ses coordonnées.
      </Warning>
    </div>
  ),
  'stop-pending-approve': (
    <div className="space-y-3">
      <p className="text-sm text-gray-600">
        Un chauffeur Premium peut proposer un nouvel arrêt directement depuis l'app mobile, ancré sur la dernière
        position GPS connue du bus. L'arrêt apparaît avec le statut <strong>"En attente"</strong> (badge jaune) dans la
        liste des arrêts, et un bloc d'information s'affiche en haut de la page dès qu'au moins un arrêt est en attente.
      </p>
      <p className="text-sm text-gray-600">
        Pour le traiter : ouvrez le menu <strong>⋯</strong> sur la ligne concernée, puis <strong>"Valider"</strong>{' '}
        (l'arrêt devient actif) ou <strong>"Rejeter"</strong> (l'arrêt devient inactif).
      </p>
      <Warning>
        Valider un arrêt ne le rattache pas automatiquement à un circuit — il faut ensuite l'assigner manuellement via
        le constructeur de circuit, comme n'importe quel autre arrêt.
      </Warning>
    </div>
  ),

  'create-circuit': (
    <div className="space-y-4">
      <p className="text-sm text-gray-600">
        Un circuit regroupe une liste d'arrêts ordonnés, un bus et un chauffeur. Il se crée en plusieurs onglets :
        Général, Arrêts, Assignation — et selon votre offre : Horaires, Optimisation, Analytics.
      </p>
      <div className="space-y-3">
        <Step number="1" title="Accéder à la section Assignations">
          Dans le menu latéral, cliquez sur <strong>Assignations</strong>, puis sur <strong>"Créer un circuit"</strong>.
        </Step>
        <Step number="2" title="Onglet Général">
          Remplissez le <strong>code</strong> (ex: NORD-01), le <strong>nom</strong> (ex: Circuit Nord - Agdal), la
          description, le type et le statut.
        </Step>
        <Step number="3" title="Onglet Arrêts">
          Ajoutez les arrêts dans l'ordre de passage (glisser-déposer). Minimum 2 arrêts.
        </Step>
        <Step number="4" title="Onglet Assignation">
          Sélectionnez le <strong>bus actif</strong> et le <strong>chauffeur</strong> assignés.
        </Step>
        <Step number="5" title="Valider">
          Cliquez sur <strong>"Enregistrer"</strong>. Le circuit est visible sur la carte de suivi.
        </Step>
      </div>
    </div>
  ),

  'circuit-fields': (
    <div className="space-y-2">
      <Field name="Code" required description="Identifiant unique — ex: NORD-01" />
      <Field name="Nom du circuit" required description="Ex: Circuit Nord - Agdal" />
      <Field name="Arrêts (min. 2)" required description="Liste ordonnée des arrêts du circuit" />
      <Field name="Type" description="Régulier / Express / Spécial" />
      <Field name="Statut" description="Actif / Inactif / Maintenance" />
      <Field name="Bus assigné" description="Bus actif sélectionné dans la liste" />
      <Field name="Chauffeur assigné" description="Chauffeur actif sélectionné dans la liste" />
    </div>
  ),

  'circuit-types': (
    <div className="space-y-2">
      <Field name="Régulier" description="Circuit quotidien standard — utilisé pour les trajets habituels" />
      <Field name="Express" description="Circuit rapide avec arrêts limités — pour les trajets prioritaires" />
      <Field name="Spécial" description="Circuit pour événements ou besoins particuliers (sorties scolaires, etc.)" />
    </div>
  ),

  'circuit-stops': (
    <div className="space-y-3">
      <p className="text-sm text-gray-600">
        <strong>Ajouter un arrêt :</strong> Dans l'onglet Arrêts, cliquez sur <strong>"Ajouter un arrêt"</strong> et
        sélectionnez-le dans la liste.
      </p>
      <p className="text-sm text-gray-600">
        <strong>Réordonner :</strong> Utilisez le glisser-déposer (icône ⠿) pour changer l'ordre de passage.
      </p>
      <p className="text-sm text-gray-600">
        <strong>Retirer un arrêt :</strong> Cliquez sur l'icône ✕ à droite de l'arrêt.
      </p>
      <Tip>La distance et la durée estimées sont calculées automatiquement en fonction du nombre d'arrêts.</Tip>
    </div>
  ),

  'assign-student': (
    <div className="space-y-3">
      <div className="space-y-3">
        <Step number="1" title="Accéder à la section Assignations">
          Dans le menu latéral, cliquez sur <strong>Assignations</strong>.
        </Step>
        <Step number="2" title="Sélectionner le circuit cible">
          Cliquez sur le circuit auquel vous souhaitez affecter l'élève.
        </Step>
        <Step number="3" title="Ajouter l'élève">
          Cliquez sur <strong>"Affecter un élève"</strong>, recherchez-le par nom, puis sélectionnez l'arrêt de montée
          et de descente.
        </Step>
        <Step number="4" title="Valider l'affectation" />
      </div>
      <Tip>
        Un élève ne peut être assigné qu'à un seul circuit à la fois. Pour le changer de circuit, supprimez d'abord son
        affectation actuelle.
      </Tip>
    </div>
  ),

  'create-driver': (
    <div className="space-y-4">
      <p className="text-sm text-gray-600">
        La gestion des chauffeurs est accessible à tous les administrateurs, offre Basic comme Premium.
      </p>
      <div className="space-y-3">
        <Step number="1" title="Accéder à la section Chauffeurs">
          Dans le menu latéral, cliquez sur <strong>Chauffeurs</strong>, puis sur{' '}
          <strong>"Ajouter un chauffeur"</strong>.
        </Step>
        <Step number="2" title="Remplir les informations">
          Complétez les champs obligatoires : prénom, nom, téléphone, email et statut.
        </Step>
        <Step number="3" title="Enregistrer">
          Cliquez sur <strong>"Enregistrer"</strong>. Le chauffeur peut ensuite être assigné à un circuit.
        </Step>
      </div>
      <Tip>En offre Premium, l'email du chauffeur lui permet de se connecter à l'application mobile chauffeur.</Tip>
    </div>
  ),

  'driver-fields': (
    <div className="space-y-2">
      <Field name="Prénom" required description="Prénom du chauffeur" />
      <Field name="Nom" required description="Nom de famille" />
      <Field name="Téléphone" required description="Format international — ex: +212 6 12 34 56 78" />
      <Field name="Email" required description="Utilisé pour la connexion à l'app mobile en Premium" />
      <Field name="Statut" required description="Actif / Inactif" />
      <Field name="Date de naissance" description="Optionnel" />
      <Field name="Genre" description="Optionnel" />
      <Field name="Adresse" description="Optionnel" />
    </div>
  ),

  'assign-driver': (
    <div className="space-y-3">
      <p className="text-sm text-gray-600">Un chauffeur s'assigne depuis le formulaire de circuit :</p>
      <div className="space-y-3">
        <Step number="1" title="Ouvrir le circuit concerné">
          Dans la section <strong>Assignations</strong>, cliquez sur le circuit ou sur son icône de modification.
        </Step>
        <Step number="2" title="Onglet Assignation">
          Sélectionnez le chauffeur dans la liste déroulante <strong>"Chauffeur assigné"</strong>.
        </Step>
        <Step number="3" title="Enregistrer" />
      </div>
      <Warning>
        Un chauffeur peut être assigné à plusieurs circuits, mais ne peut en conduire qu'un à la fois. Vérifiez les
        horaires pour éviter les conflits.
      </Warning>
    </div>
  ),

  'driver-app': (
    <div className="space-y-3">
      <p className="text-sm text-gray-600">
        L'application mobile chauffeur est une fonctionnalité <strong>Premium</strong>. Elle donne accès à :
      </p>
      <div className="space-y-2">
        {[
          'Dashboard trajet : liste des arrêts, élèves attendus, ETA en temps réel',
          'Pointage montée / descente élève à chaque arrêt',
          "Auto-pickup : prise en charge automatique si l'arrêt est validé sans action manuelle",
          'Réception en temps réel des absences déclarées par les parents',
          "Signalement d'incident avec notification immédiate aux parents concernés",
        ].map((item, i) => (
          <div key={i} className="flex items-start gap-2">
            <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
            <p className="text-sm text-gray-600">{item}</p>
          </div>
        ))}
      </div>
      <Tip>
        Le chauffeur se connecte avec son email/mot de passe sur la même application mobile que les parents. Le rôle est
        détecté automatiquement à la connexion.
      </Tip>
    </div>
  ),

  'create-bus': (
    <div className="space-y-4">
      <p className="text-sm text-gray-600">La gestion de la flotte est accessible à tous les administrateurs.</p>
      <div className="space-y-3">
        <Step number="1" title="Accéder à la section Bus">
          Dans le menu latéral, cliquez sur <strong>Bus</strong>, puis sur <strong>"Ajouter un bus"</strong>.
        </Step>
        <Step number="2" title="Onglet Général">
          Remplissez l'immatriculation, le modèle, la capacité et le statut.
        </Step>
        <Step number="3" title="Renseigner l'IMEI du traceur GPS">
          Si le bus est équipé d'un boîtier GPS, saisissez l'
          <strong>IMEI</strong> dans le champ dédié. C'est ce qui permet au système de lier les positions reçues à ce
          bus.
        </Step>
        <Step number="4" title="Enregistrer" />
      </div>
    </div>
  ),

  'bus-fields': (
    <div className="space-y-2">
      <Field name="Immatriculation" required description="Ex: 12345-A-1" />
      <Field name="Modèle" required description="Ex: Mercedes Sprinter" />
      <Field name="Capacité" required description="Nombre de places élèves" />
      <Field name="Statut" description="Actif / Inactif / Maintenance" />
      <Field name="Marque" description="Optionnel" />
      <Field name="Année" description="Optionnel" />
      <Field
        name="IMEI / ID Traceur GPS"
        description="Numéro IMEI du boîtier GPS — indispensable pour le suivi en temps réel"
      />
      <Field name="Carburant" description="Diesel / Essence / GPL / Électrique" />
    </div>
  ),

  'bus-gps': (
    <div className="space-y-3">
      <p className="text-sm text-gray-600">
        Le lien entre le boîtier GPS physique et le bus se fait via l'
        <strong>IMEI</strong> (identifiant unique du traceur, 15 chiffres) :
      </p>
      <div className="space-y-3">
        <Step number="1" title="Trouver l'IMEI du boîtier">
          L'IMEI est inscrit sur l'étiquette du boîtier GPS ou accessible depuis l'interface Traccar.
        </Step>
        <Step number="2" title="Saisir l'IMEI dans la fiche du bus">
          Dans la section Bus, ouvrez le bus concerné et renseignez le champ <strong>"IMEI / ID Traceur GPS"</strong>.
        </Step>
        <Step number="3" title="Enregistrer">
          Le système associe automatiquement les positions reçues de ce traceur à ce bus.
        </Step>
      </div>
      <Tip>Sans IMEI renseigné, le bus ne recevra pas de positions GPS et n'apparaîtra pas sur la carte de suivi.</Tip>
    </div>
  ),

  'bus-offline': (
    <div className="space-y-3">
      <p className="text-sm text-gray-600">
        Un bus passe en statut <strong>"hors ligne"</strong> si aucun signal GPS n'a été reçu depuis plus de{' '}
        <strong>15 minutes</strong>. Vérifiez dans l'ordre :
      </p>
      <div className="space-y-2">
        {[
          'Le boîtier GPS est allumé et alimenté (fil ACC connecté)',
          'La connexion 4G du boîtier est active',
          "L'IMEI du boîtier est correctement renseigné dans la fiche du bus",
          "Le bus est à l'extérieur avec une bonne réception GPS",
        ].map((item, i) => (
          <div key={i} className="flex items-start gap-2">
            <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
            <p className="text-sm text-gray-600">{item}</p>
          </div>
        ))}
      </div>
      <Warning>Si le problème persiste après vérification, contactez notre support technique.</Warning>
    </div>
  ),

  'how-gps': (
    <div className="space-y-3">
      <p className="text-sm text-gray-600 leading-relaxed">
        Chaque bus est équipé d'un boîtier GPS qui transmet sa position en temps réel via une connexion 4G sécurisée. Le
        dashboard affiche la position de tous les bus actifs sur une carte interactive, mise à jour automatiquement.
      </p>
      <p className="text-sm text-gray-600">
        L'indicateur de fraîcheur du signal visible sur chaque carte de bus indique depuis combien de temps la dernière
        position a été reçue.
      </p>
      <Tip>
        Pour voir le suivi en temps réel, accédez à la section <strong>Suivi</strong> dans le menu latéral. Les bus en
        service apparaissent sous forme d'icônes colorées selon leur statut.
      </Tip>
    </div>
  ),

  'bus-not-on-map': (
    <div className="space-y-3">
      <p className="text-sm text-gray-600">Vérifiez les points suivants dans l'ordre :</p>
      <div className="space-y-2">
        {[
          'Le bus est actif dans la section Bus (statut = Actif)',
          "L'IMEI du traceur GPS est renseigné dans la fiche du bus",
          'Le bus est assigné à un circuit actif',
          "L'appareil GPS est allumé et connecté",
          'Un trajet est en cours',
        ].map((item, i) => (
          <div key={i} className="flex items-start gap-2">
            <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
            <p className="text-sm text-gray-600">{item}</p>
          </div>
        ))}
      </div>
      <Warning>Si le problème persiste après vérification, contactez notre support technique.</Warning>
    </div>
  ),

  'trip-history': (
    <div className="space-y-3">
      <p className="text-sm text-gray-600 leading-relaxed">
        Dans la section <strong>Suivi GPS</strong> du menu latéral, accédez à la page <strong>Suivi GPS</strong> puis
        utilisez le lien <strong>Historique</strong> pour consulter les trajets passés. Filtrez par bus, par circuit et
        par date pour retrouver un trajet et le rejouer sur la carte.
      </p>
      <Warning>
        L'historique des trajets est une fonctionnalité <strong>Premium</strong>. Les données sont conservées 365 jours
        (30 jours pour les positions GPS brutes en Basic).
      </Warning>
    </div>
  ),

  'parent-confirmation-check': (
    <div className="space-y-3">
      <p className="text-sm text-gray-600 leading-relaxed">
        Ce check ✓ indique que le <strong>parent</strong> a lui-même confirmé, depuis son application, que son enfant
        est bien monté ou a bien été récupéré — en tapant sur « Enfant(s) à bord ? » / « Enfant(s) récupéré(s) » sur son
        écran de suivi en direct. Survolez (ou touchez sur tablette) le check pour voir l'heure exacte de la
        confirmation.
      </p>
      <Warning>
        Ce signal est <strong>purement informatif</strong> et ne remplace jamais le pointage officiel GPS ou chauffeur
        affiché à côté (statut Présent/Absent). Il couvre un cas que le système ne peut pas détecter automatiquement :
        un ramassage ou une dépose convenu(e) directement avec l'école à un autre endroit que l'arrêt habituel.
        Disponible sur les deux offres (Basic et Premium).
      </Warning>
    </div>
  ),

  'manual-notif': (
    <div className="space-y-3">
      <div className="space-y-3">
        <Step number="1" title="Accéder à la section Notifications">
          Dans le menu latéral, cliquez sur <strong>Notifications</strong> (fonctionnalité <strong>Premium</strong>).
        </Step>
        <Step number="2" title="Nouvelle annonce">
          Cliquez sur <strong>"Nouvelle annonce"</strong>, puis choisissez le destinataire : <strong>Parents</strong> ou{' '}
          <strong>Chauffeurs</strong>.
        </Step>
        <Step number="3" title="Remplir le message">
          Entrez le <strong>titre</strong> et le <strong>contenu</strong> du message (texte libre).
        </Step>
        <Step number="4" title="Choisir l'audience">
          Selon le destinataire choisi : toute l'école, un ou plusieurs circuits, un ou plusieurs arrêts, ou{' '}
          <strong>Personnalisé</strong> pour sélectionner des parents ou chauffeurs précis dans une liste.
        </Step>
        <Step number="5" title="Envoyer">
          Une confirmation récapitule le nombre de destinataires avant l'envoi. Les parents et chauffeurs reçoivent une
          notification push sur leur smartphone, visible aussi dans l'app (onglet Notifications).
        </Step>
      </div>
      <Tip>
        Les destinataires doivent avoir installé l'application M'EyeBus et activé les notifications sur leur téléphone.
        Le suivi de lecture (page "Notifications envoyées") fonctionne pour les deux audiences.
      </Tip>
    </div>
  ),

  'auto-notifs': (
    <div className="space-y-2">
      <p className="text-sm text-gray-600 mb-3">
        Certaines notifications sont automatiques dès l'offre <strong>Basic</strong>, d'autres nécessitent l'offre{' '}
        <strong>Premium</strong>.
      </p>
      <Field name="Départ du bus" description="Basic — envoyée aux parents quand le bus démarre son trajet" />
      <Field
        name="Approche de l'arrêt"
        description="Basic — alerte quand le bus approche à moins de 150 m de l'arrêt"
      />
      <Field name="Arrivée à l'école" description="Basic — envoyée à l'arrivée du bus à l'école" />
      <Field name="Élève monté dans le bus" description="Premium — confirmation pointée par le chauffeur via l'app" />
      <Field name="Élève déposé à l'arrêt" description="Premium — confirmation pointée par le chauffeur via l'app" />
      <Field name="Signalement d'incident" description="Premium — alerte si le chauffeur signale un incident" />
    </div>
  ),

  'notif-tiers': (
    <div className="space-y-3">
      <p className="text-sm text-gray-600">
        <strong>Basic</strong> — notifications automatiques basées sur le GPS : départ du bus, approche de l'arrêt
        (géofencing 150 m), arrivée à l'école. Aucune action requise de la part du chauffeur.
      </p>
      <p className="text-sm text-gray-600">
        <strong>Premium</strong> — tout le Basic, plus les confirmations individuelles pointées par le chauffeur
        (montée, descente, incident) et le centre de notifications pour envoyer des messages manuels aux parents.
      </p>
      <Tip>
        Les confirmations de montée et de descente nécessitent que le chauffeur utilise l'application mobile. Elles ne
        sont pas disponibles avec un traceur GPS seul.
      </Tip>
    </div>
  ),

  'holidays-notifs': (
    <div className="space-y-3">
      <p className="text-sm text-gray-600">
        Si les parents ne reçoivent aucune notification un jour donné, la première chose à vérifier est la configuration
        des <strong>vacances scolaires</strong>.
      </p>
      <p className="text-sm text-gray-600">
        Pendant une période de vacances configurée, le système bloque automatiquement :
      </p>
      <div className="space-y-2">
        {[
          'Le démarrage automatique des trajets (via GPS IoT)',
          "Les notifications d'approche, d'arrivée et de départ",
          'La notification « trajet démarré » envoyée aux parents',
        ].map((item, i) => (
          <div key={i} className="flex items-start gap-2">
            <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
            <p className="text-sm text-gray-600">{item}</p>
          </div>
        ))}
      </div>
      <Tip>
        Vérifiez l'onglet <strong>Paramètres → Vacances scolaires</strong>. Si une période est marquée « En cours »,
        toutes les notifications sont suspendues jusqu'à la fin de cette période.
      </Tip>
    </div>
  ),

  'live-eta': (
    <div className="space-y-3">
      <p className="text-sm text-gray-600">
        Pendant un trajet, le parent voit l'avancée du bus vers l'arrêt de son enfant directement sur l'écran verrouillé
        de son téléphone, <strong>sans ouvrir l'application</strong> — même si elle est fermée.
      </p>
      <div className="space-y-2">
        {[
          "iPhone (iOS 16.1 et plus) : sur l'écran verrouillé et dans la Dynamic Island. Démarre tout seul sur iOS 17.2 et plus, à l'ouverture de l'écran de suivi en dessous.",
          "Android 16 et plus : notification « en direct » sur l'écran verrouillé, avec le temps restant dans la barre d'état.",
          "À l'aller : le suivi continue après le passage à l'arrêt (« Vers l'école ») et se ferme à l'arrivée à l'école.",
          "Au retour : il se ferme dès que l'arrêt de l'enfant est passé.",
        ].map((item, i) => (
          <div key={i} className="flex items-start gap-2">
            <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
            <p className="text-sm text-gray-600">{item}</p>
          </div>
        ))}
      </div>
      <Tip>
        Sur les téléphones Android antérieurs à Android 16, ou si le parent a désactivé les « Live updates » ou les «
        Activités en direct » dans les réglages de l'application, ce suivi ne s'affiche pas. Le parent reçoit toujours
        les notifications habituelles (départ, approche, arrivée) et peut suivre le bus dans l'application.
      </Tip>
    </div>
  ),

  'admin-realtime-notif': (
    <div className="space-y-3">
      <p className="text-sm text-gray-600">
        À la connexion, le navigateur demande l'autorisation d'afficher des <strong>notifications</strong>. En
        l'acceptant, l'admin reçoit instantanément un badge sur la cloche du tableau de bord et une notification
        navigateur (même onglet fermé) pour :
      </p>
      <Field name="Incident signalé par un chauffeur" description="Priorité urgente" />
      <Field name="Trajet non démarré depuis plus de 10 minutes" description="Priorité haute" />
      <Field name="Demande de transport soumise par un parent" description="Priorité normale" />
      <Field name="Nouvel arrêt proposé par un chauffeur" description="Priorité normale" />
      <Tip>
        Si la popup d'autorisation n'apparaît pas, cliquez n'importe où sur la page — les navigateurs bloquent la
        demande sans interaction utilisateur préalable.
      </Tip>
      <Warning>
        Sur iPhone/iPad, ces notifications nécessitent l'installation du dashboard en <strong>PWA</strong> (icône «
        Ajouter à l'écran d'accueil » depuis Safari) — Safari ne les supporte pas dans un onglet classique.
      </Warning>
    </div>
  ),

  'holidays-setup': (
    <div className="space-y-3">
      <p className="text-sm text-gray-600">
        Rendez-vous dans <strong>Paramètres → Vacances scolaires</strong> pour gérer les périodes pendant lesquelles
        aucun trajet ne démarre et aucune notification n'est envoyée.
      </p>
      <div className="space-y-2">
        {[
          'Cliquez sur « Ajouter une période » et saisissez un intitulé (ex : Vacances de printemps), une date de début et une date de fin.',
          'Choisissez le type : Vacances scolaires, Jour férié ou Exceptionnel.',
          "La période est active dès l'enregistrement — aucune autre action n'est nécessaire.",
          'Vous pouvez modifier ou supprimer une période à tout moment. Les périodes passées restent visibles en archive.',
        ].map((item, i) => (
          <div key={i} className="flex items-start gap-2">
            <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
            <p className="text-sm text-gray-600">{item}</p>
          </div>
        ))}
      </div>
      <Tip>
        L'app mobile parent affiche automatiquement une bannière « Pas de trajet aujourd'hui » lorsque votre école est
        en période de vacances.
      </Tip>
    </div>
  ),

  dashboard: (
    <div className="space-y-3">
      <p className="text-sm text-gray-600">
        Le tableau de bord analytique (section <strong>Dashboard</strong>) est disponible en offre{' '}
        <strong>Premium</strong>. Il regroupe :
      </p>
      <div className="space-y-2">
        {[
          'KPIs en temps réel : trajets en cours, élèves pris en charge, taux de ponctualité',
          'Graphiques de tendance : activité par jour, par circuit, par période',
          'Suivi des performances par chauffeur',
          "Vue d'ensemble flotte : statut de chaque bus en un coup d'œil",
        ].map((item, i) => (
          <div key={i} className="flex items-start gap-2">
            <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
            <p className="text-sm text-gray-600">{item}</p>
          </div>
        ))}
      </div>
      <Tip>Le dashboard se met à jour en temps réel. Actualisez la page si les données semblent en retard.</Tip>
    </div>
  ),

  reports: (
    <div className="space-y-3">
      <p className="text-sm text-gray-600">
        La section <strong>Rapports</strong> (offre <strong>Premium</strong>) permet de consulter et d'exporter les
        statistiques de votre flotte :
      </p>
      <div className="space-y-2">
        {[
          'Historique des trajets avec horaires réels vs prévus',
          'Statistiques de ponctualité par circuit et par chauffeur',
          'Taux de présence élèves sur une période donnée',
          'Export des données en CSV',
        ].map((item, i) => (
          <div key={i} className="flex items-start gap-2">
            <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
            <p className="text-sm text-gray-600">{item}</p>
          </div>
        ))}
      </div>
    </div>
  ),

  requests: (
    <div className="space-y-3">
      <p className="text-sm text-gray-600">
        La section <strong>Demandes</strong> (offre <strong>Premium</strong>) centralise les demandes soumises par les
        parents depuis leur application mobile :
      </p>
      <div className="space-y-2">
        <Field
          name="Déclaration d'absence"
          description="Le parent signale que son enfant ne prendra pas le bus. Le chauffeur voit un bandeau « Élèves absents » en lecture seule sur son trajet du jour (pas de notification push au chauffeur)."
        />
        <Field
          name="Changement d'arrêt"
          description="Le parent demande un changement d'arrêt ponctuel (un jour précis) ou permanent. Pour un changement ponctuel approuvé, le chauffeur voit un bandeau « Changement aujourd'hui » ; rien n'est appliqué automatiquement en base, l'admin gère la suite manuellement."
        />
      </div>
      <div className="space-y-3 mt-3">
        <Step number="1" title="Accéder à la section Demandes">
          Dans le menu latéral, cliquez sur <strong>Demandes</strong>.
        </Step>
        <Step number="2" title="Consulter les demandes en attente">
          Les nouvelles demandes apparaissent avec un badge. Cliquez sur une demande pour voir les détails.
        </Step>
        <Step number="3" title="Valider ou refuser">
          Cliquez sur <strong>"Approuver"</strong> (une note optionnelle) ou <strong>"Refuser"</strong> (motif
          obligatoire). Le parent reçoit une notification de la décision.
        </Step>
      </div>
    </div>
  ),

  'tier-comparison': (
    <div className="overflow-x-auto">
      <table className="w-full text-sm border-collapse">
        <thead>
          <tr className="bg-gray-100">
            <th className="text-left px-3 py-2 font-semibold text-gray-700">Fonctionnalité</th>
            <th className="text-center px-3 py-2 font-semibold text-gray-700">Basic</th>
            <th className="text-center px-3 py-2 font-semibold text-amber-700">Premium</th>
          </tr>
        </thead>
        <tbody>
          {[
            ['Suivi GPS temps réel (traceur IoT)', true, true],
            ['Gestion élèves & parents', true, true],
            ['Gestion arrêts, circuits, chauffeurs, bus', true, true],
            ['Notifications automatiques GPS (départ, arrêt, arrivée)', true, true],
            ['App mobile parent — carte live & ETA', true, true],
            ['App mobile chauffeur', false, true],
            ['Confirmation montée / descente élève', false, true],
            ["Déclaration d'absence en 2 taps", false, true],
            ['Centre de notifications manuel', false, true],
            ['Tableau de bord analytique', false, true],
            ['Rapports & statistiques de flotte', false, true],
            ['Gestion des demandes parents', false, true],
            ['Historique trajets (365 jours)', false, true],
          ].map(([feature, basic, premium]) => (
            <tr key={feature} className="border-t border-gray-100">
              <td className="px-3 py-2 text-gray-700">{feature}</td>
              <td className="px-3 py-2 text-center">{basic ? '✅' : '—'}</td>
              <td className="px-3 py-2 text-center">{premium ? '✅' : '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <Tip>
        Basic = traceur GPS IoT uniquement. Premium = traceur GPS + application mobile chauffeur pour le pointage
        élèves, la gestion des absences, le suivi des arrêts en temps réel et le signalement d'incidents.
      </Tip>
    </div>
  ),

  'tier-limits': (
    <div className="space-y-3">
      <p className="text-sm text-gray-600">Chaque offre impose des limites sur le nombre d'entités gérées :</p>
      <div className="overflow-x-auto">
        <table className="w-full text-sm border-collapse">
          <thead>
            <tr className="bg-gray-100">
              <th className="text-left px-3 py-2 font-semibold text-gray-700">Limite</th>
              <th className="text-center px-3 py-2 font-semibold text-gray-700">Basic</th>
              <th className="text-center px-3 py-2 font-semibold text-amber-700">Premium</th>
            </tr>
          </thead>
          <tbody>
            {[
              ['Élèves maximum', '100', '300'],
              ['Chauffeurs maximum', '5', '20'],
              ['Rétention historique GPS', '30 jours', '365 jours'],
            ].map(([label, basic, premium]) => (
              <tr key={label} className="border-t border-gray-100">
                <td className="px-3 py-2 text-gray-700">{label}</td>
                <td className="px-3 py-2 text-center text-gray-600">{basic}</td>
                <td className="px-3 py-2 text-center text-amber-700 font-medium">{premium}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <Warning>
        Si vous atteignez la limite de votre offre, contactez-nous pour mettre à niveau votre abonnement.
      </Warning>
    </div>
  ),

  'reset-password': (
    <div className="space-y-3">
      <Step number="1" title="Sur la page de connexion">
        Cliquez sur <strong>"Mot de passe oublié ?"</strong>.
      </Step>
      <Step number="2" title="Entrer votre email">
        Saisissez l'adresse email de votre compte administrateur.
      </Step>
      <Step number="3" title="Consulter votre boîte mail">
        Vous recevrez un email avec un lien de réinitialisation valable <strong>24 heures</strong>.
      </Step>
      <Step number="4" title="Définir un nouveau mot de passe">
        Cliquez sur le lien et choisissez un nouveau mot de passe sécurisé.
      </Step>
    </div>
  ),

  'multi-device': (
    <div className="space-y-3">
      <p className="text-sm text-gray-600 leading-relaxed">
        Le dashboard M'EyeBus est accessible depuis n'importe quel navigateur web (Chrome, Firefox, Safari, Edge) sur
        ordinateur, tablette ou smartphone. Connectez-vous simplement avec vos identifiants administrateur.
      </p>
      <Tip>
        Pour une expérience optimale, utilisez un écran d'au moins 13 pouces. Sur mobile, certaines fonctionnalités
        (carte, tableaux) sont mieux utilisables en mode paysage.
      </Tip>
    </div>
  ),
};

// ─── Main component ───────────────────────────────────────────────────────────

const FAQ = () => {
  const [activeSection, setActiveSection] = useState('students');
  const [openItems, setOpenItems] = useState(new Set());
  const [query, setQuery] = useState('');
  const [showBackToTop, setShowBackToTop] = useState(false);
  const topSentinelRef = useRef(null);

  const normalizedQuery = query.toLowerCase().trim();
  const isSearching = normalizedQuery.length > 0;

  const matchingItems = isSearching
    ? ALL_ITEMS.filter(
        (item) =>
          item.question.toLowerCase().includes(normalizedQuery) ||
          (item.keywords || '').toLowerCase().includes(normalizedQuery),
      )
    : [];

  const itemsForSection = (sectionId) => ALL_ITEMS.filter((item) => item.section === sectionId);

  const toggleItem = (id) => {
    setOpenItems((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const expandAll = () => setOpenItems(new Set(ALL_ITEMS.map((i) => i.id)));
  const collapseAll = () => setOpenItems(new Set());

  // Back-to-top visibility via sentinel
  useEffect(() => {
    const sentinel = topSentinelRef.current;
    if (!sentinel) return;
    const obs = new IntersectionObserver(([entry]) => setShowBackToTop(!entry.isIntersecting), { threshold: 0 });
    obs.observe(sentinel);
    return () => obs.disconnect();
  }, []);

  const scrollToTop = () =>
    topSentinelRef.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    });

  // Sync sidebar highlight with scroll position
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries.filter((e) => e.isIntersecting);
        if (visible.length > 0) setActiveSection(visible[0].target.id);
      },
      { rootMargin: '-10% 0px -75% 0px', threshold: 0 },
    );
    SECTIONS.forEach(({ id }) => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, []);

  // Open and scroll to item from URL hash on mount
  useEffect(() => {
    const hash = window.location.hash.slice(1);
    if (hash) {
      setOpenItems(new Set([hash]));
      setTimeout(() => {
        document.getElementById(hash)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 150);
    }
  }, []);

  const scrollTo = (id) => {
    setActiveSection(id);
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const renderItem = (item) => (
    <AccordionItem
      key={item.id}
      id={item.id}
      question={item.question}
      premium={item.premium}
      open={openItems.has(item.id)}
      onToggle={() => toggleItem(item.id)}
    >
      {CONTENT[item.id]}
    </AccordionItem>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      <Helmet>
        <title>FAQ — M'EyeBus</title>
        <meta
          name="description"
          content="Questions fréquentes sur M'EyeBus : suivi GPS, notifications parents, gestion de flotte, offres Basic et Premium."
        />
      </Helmet>
      {/* Sentinel pour détecter le scroll — doit être le premier élément */}
      <div ref={topSentinelRef} aria-hidden="true" />

      {/* ── Header ── */}
      <div className="bg-white border-b border-gray-200">
        <div className="px-6 py-8">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <div className="flex items-center space-x-3 mb-2">
                <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                  <Headphones className="w-5 h-5 text-blue-600" />
                </div>
                <h1 className="text-2xl font-bold text-gray-900">Guide utilisateur M'EyeBus</h1>
              </div>
              <p className="text-gray-500 text-sm max-w-2xl">
                Instructions pour utiliser le dashboard d'administration : élèves, chauffeurs, bus, circuits, suivi GPS
                et notifications.
              </p>
              <p className="text-xs text-gray-400 mt-1">Mis à jour le 21 mai 2026</p>
            </div>
            <button
              onClick={openItems.size > 0 ? collapseAll : expandAll}
              className="text-xs font-medium text-blue-600 hover:text-blue-700 px-3 py-1.5 rounded-lg border border-blue-200 hover:bg-blue-50 transition-colors whitespace-nowrap self-start mt-1"
            >
              {openItems.size > 0 ? 'Tout réduire' : 'Tout développer'}
            </button>
          </div>

          {/* Search bar */}
          <div className="mt-5 relative max-w-lg">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
            <input
              type="text"
              placeholder="Rechercher dans le guide…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="w-full pl-9 pr-9 py-2.5 text-sm bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            {query && (
              <button
                onClick={() => setQuery('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>

        {/* Mobile nav — horizontal scroll */}
        <div className="lg:hidden overflow-x-auto border-t border-gray-100">
          <div className="flex gap-1 px-4 py-2 min-w-max">
            {SECTIONS.map(({ id, label, icon: Icon }) => (
              <button
                key={id}
                onClick={() => scrollTo(id)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium whitespace-nowrap transition-colors ${
                  activeSection === id ? 'bg-blue-100 text-blue-700' : 'text-gray-500 hover:bg-gray-100'
                }`}
              >
                <Icon className="w-3.5 h-3.5" />
                {label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="px-6 py-10 flex gap-10">
        {/* ── Sidebar (desktop) ── */}
        <aside className="hidden lg:block w-56 flex-shrink-0">
          <div className="sticky top-6 space-y-1">
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Sections</p>
            {SECTIONS.map(({ id, label, icon: Icon }) => (
              <button
                key={id}
                onClick={() => scrollTo(id)}
                className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm transition-colors text-left ${
                  activeSection === id ? 'bg-blue-50 text-blue-700 font-medium' : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                <Icon className="w-4 h-4 flex-shrink-0" />
                <span className="flex-1">{label}</span>
                <span className={`text-xs tabular-nums ${activeSection === id ? 'text-blue-400' : 'text-gray-400'}`}>
                  {itemsForSection(id).length}
                </span>
              </button>
            ))}
          </div>
        </aside>

        {/* ── Content ── */}
        <main className="flex-1 space-y-10 min-w-0">
          {/* Search results */}
          {isSearching ? (
            <div className="space-y-4">
              {matchingItems.length === 0 ? (
                <div className="text-center py-16 text-gray-400">
                  <Search className="w-10 h-10 mx-auto mb-3 opacity-30" />
                  <p className="text-sm font-medium">Aucun résultat pour « {query} »</p>
                  <p className="text-xs mt-1">
                    Essayez d'autres mots-clés ou{' '}
                    <button onClick={() => setQuery('')} className="text-blue-500 hover:underline">
                      parcourez les sections
                    </button>
                  </p>
                </div>
              ) : (
                <>
                  <p className="text-sm text-gray-500">
                    {matchingItems.length} résultat
                    {matchingItems.length > 1 ? 's' : ''} pour « {query} »
                  </p>
                  {matchingItems.map((item, idx) => {
                    const section = SECTIONS.find((s) => s.id === item.section);
                    const isNewSection = idx === 0 || item.section !== matchingItems[idx - 1].section;
                    return (
                      <div key={item.id}>
                        {isNewSection && section && (
                          <p
                            className={`text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2 ml-1 ${idx > 0 ? 'mt-6' : ''}`}
                          >
                            {section.label}
                          </p>
                        )}
                        {renderItem(item)}
                      </div>
                    );
                  })}
                </>
              )}
            </div>
          ) : (
            /* Normal sections view */
            SECTIONS.map(({ id, label, icon: Icon, iconColor, bgColor }) => (
              <section key={id} id={id} className="scroll-mt-4">
                <div className="flex items-center gap-3 mb-6">
                  <div className={`w-9 h-9 ${bgColor} rounded-lg flex items-center justify-center`}>
                    <Icon className={`w-5 h-5 ${iconColor}`} />
                  </div>
                  <h2 className="text-xl font-bold text-gray-900">{label}</h2>
                </div>
                <div className="space-y-3">{itemsForSection(id).map(renderItem)}</div>
              </section>
            ))
          )}

          {/* Contact */}
          {!isSearching && (
            <div className="bg-white rounded-xl border border-gray-200 p-8">
              <h2 className="text-lg font-semibold text-gray-900 mb-2">Vous n'avez pas trouvé votre réponse ?</h2>
              <p className="text-sm text-gray-500 mb-6">Notre équipe support est disponible pour vous aider.</p>
              <div className="flex flex-col sm:flex-row gap-3">
                <a
                  href="mailto:contact@meyebus.ma"
                  className="flex items-center justify-center space-x-2 px-5 py-3 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors"
                >
                  <Mail className="w-4 h-4" />
                  <span>Envoyer un email</span>
                </a>
                <a
                  href="https://wa.me/212631387567"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center space-x-2 px-5 py-3 bg-green-500 text-white text-sm font-medium rounded-lg hover:bg-green-600 transition-colors"
                >
                  <MessageCircle className="w-4 h-4" />
                  <span>Contacter sur WhatsApp</span>
                </a>
              </div>
            </div>
          )}
        </main>
      </div>

      {/* Bouton retour en haut */}
      <AnimatePresence>
        {showBackToTop && (
          <motion.button
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 12 }}
            onClick={scrollToTop}
            className="fixed bottom-6 right-6 z-50 p-3 bg-gray-900 text-white rounded-full shadow-lg hover:bg-gray-700 transition-colors"
            title="Retour en haut"
          >
            <ChevronDown className="w-4 h-4 rotate-180" />
          </motion.button>
        )}
      </AnimatePresence>
    </div>
  );
};

export default FAQ;
